// SocialLinksForm.js
import React, { useState } from 'react';

const socialOptions = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'github', label: 'GitHub' },
  { value: 'dribbble', label: 'Dribbble' },
  { value: 'website', label: 'Website' },
];

// Helper function to validate and normalize URLs
const normalizeUrl = (url) => {
  if (!url) return '';
  
  // If the URL doesn't start with http:// or https://, add https://
  if (!url.match(/^https?:\/\//i)) {
    return `https://${url}`;
  }
  
  return url;
};

const SocialLinksForm = ({ socialLinks, setSocialLinks }) => {
  const [errors, setErrors] = useState({});
  
  const validateUrl = (url) => {
    if (!url) return true; // Empty is allowed
    
    try {
      // Try to construct a URL object (validates format)
      new URL(normalizeUrl(url));
      return true;
    } catch (e) {
      return false;
    }
  };
  
  const handleChange = (index, field, value) => {
    const updatedLinks = [...socialLinks];
    
    // If we're changing the URL field, clear any errors for this index
    if (field === 'url') {
      setErrors({...errors, [index]: null});
    }
    
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    setSocialLinks(updatedLinks);
  };

  const handleAddLink = () => {
    if (socialLinks.length < 3) {
      setSocialLinks([...socialLinks, { type: 'website', url: '' }]);
    }
  };

  const handleRemoveLink = (index) => {
    const updatedLinks = socialLinks.filter((_, i) => i !== index);
    setSocialLinks(updatedLinks);
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-2">Social Media Links</h3>
      {socialLinks.map((link, index) => (
        <div key={index} className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-4">
          <select
            value={link.type}
            onChange={(e) => handleChange(index, 'type', e.target.value)}
            className="border border-gray-300 rounded p-2"
          >
            {socialOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="w-full">
            <input
              type="url"
              placeholder="linkedin.com/in/yourprofile"
              value={link.url}
              onChange={(e) => handleChange(index, 'url', e.target.value)}
              className={`border ${errors[index] ? 'border-red-500' : 'border-gray-300'} rounded p-2 w-full`}
              onBlur={() => {
                // Validate on blur
                if (link.url && !validateUrl(link.url)) {
                  setErrors({...errors, [index]: "Please enter a valid URL"});
                } else if (link.url) {
                  // Normalize the URL on blur if it's valid
                  const normalizedUrl = normalizeUrl(link.url);
                  if (normalizedUrl !== link.url) {
                    handleChange(index, 'url', normalizedUrl);
                  }
                }
              }}
            />
            {errors[index] && <p className="text-red-500 text-xs mt-1">{errors[index]}</p>}
            <p className="text-xs text-gray-500 mt-1">URLs will be automatically prefixed with https:// if needed</p>
          </div>
          <button
            type="button"
            onClick={() => handleRemoveLink(index)}
            className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
          >
            Remove
          </button>
        </div>
      ))}
      {socialLinks.length < 3 && (
        <button
          type="button"
          onClick={handleAddLink}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors duration-200"
        >
          Add Social Link
        </button>
      )}
    </div>
  );
};

export default SocialLinksForm;
